import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Apollo, gql } from 'apollo-angular';
import { DesktopToastService } from '../../services/desktop-toast.service';
import { CatchError } from 'projects/shared/src/lib/classes/catch-error';
import { TenantDefectOutput } from 'projects/shared/src/lib/graphql/output/tenantDefectOutput';
import {
  AssetDefectsQueryArgs,
  AssetDefectsQueryRoot,
  ReportDefectMutationArgs,
  VerifyDefectMutatioNArgs,
  VerifyDefectMutationRoot,
  reportDefectMutationRoot,
} from 'projects/shared/src/lib/graphql/crud/tenantDefect';
import { firstValueFrom } from 'rxjs';
import { FULL_FRAGMENT_TENANT_DEFECT } from 'projects/shared/src/lib/graphql/fragments/fullFragmentTenantDefect';
import { DefectType } from 'projects/shared/src/lib/graphql/enums/defectType';
import { v4 } from 'uuid';
import { LocaleService } from 'projects/shared/src/lib/services/locale.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
  LocalEventData_AssetDefect,
  LocalEventService,
  LocalEventType,
} from '../../services/local-event.service';
import { AppModule } from '../../app.module';

export type ReportDefectDialogData = {
  assetId: string;
  tryToVerify: boolean;
};

export type ReportDefectDialogResult = TenantDefectOutput | undefined;

class SimpleItem {
  constructor(public id: number, public name: string, public subname?: string) { }
}

@Component({
  selector: 'app-report-defect-dialog',
  templateUrl: './report-defect-dialog.component.html',
  styleUrls: ['./report-defect-dialog.component.scss'],
})
export class ReportDefectDialogComponent implements OnInit {
  loading = false;
  activity = false;
  assetDefects: TenantDefectOutput[] = [];
  readonly kinds: Map<number, SimpleItem> = new Map<number, SimpleItem>([
    [2, new SimpleItem(2, 'verified')],
    [1, new SimpleItem(1, 'reported')],
  ]);
  readonly defectOptions = new Map<number, SimpleItem>([
    [4, new SimpleItem(4, 'FROZEN', 'frozen')],
    [3, new SimpleItem(3, 'DEFECT 100', 'nonfunctional')],
    [2, new SimpleItem(2, 'DEFECT', 'partially functional')],
    [1, new SimpleItem(1, 'OK', 'fully functional')],
  ]);

  selectedKind: SimpleItem | undefined;
  selectedDefectOption: SimpleItem | undefined;
  comment: string | undefined;
  readonly uuid = v4();

  get canApply(): boolean {
    return !!this.selectedKind && !!this.selectedDefectOption && !!this.comment;
  }

  constructor(
    private _dialogRef: MatDialogRef<ReportDefectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportDefectDialogData,
    private _apollo: Apollo,
    private _toastService: DesktopToastService,
    @Inject(MAT_DATE_LOCALE) public locale: string,
    public localeService: LocaleService,
    private _localEventService: LocalEventService
  ) { }

  ngOnInit(): void {
    this.#loadData();
  }

  closeDialog() {
    this._dialogRef.close(undefined);
  }

  async apply() {
    if (!this.canApply) {
      return;
    }

    if (this.selectedKind?.id === DefectType.Verified) {
      await this.#verify();
    } else {
      this.#report();
    }
  }

  async #loadData() {
    this.loading = true;
    try {
      const variables: AssetDefectsQueryArgs = {
        assetId: this.data.assetId,
      };

      const result = await firstValueFrom(
        this._apollo.query<AssetDefectsQueryRoot>({
          query: gql`
            ${FULL_FRAGMENT_TENANT_DEFECT}
            query AssetDefects($assetId: String!) {
              assetDefects(assetId: $assetId) {
                ...FullFragmentTenantDefect
              }
            }
          `,
          variables,
          fetchPolicy: 'network-only',
        })
      );
      this.assetDefects = result.data.assetDefects.sortBy((x) => x.timestamp, 'desc');
      this.#preselect();
    } catch (error) {
      this._toastService.error(new CatchError(error).message, 'ERROR');
      this._dialogRef.close(undefined);
    } finally {
      this.loading = false;
    }
  }

  #preselect() {
    if (!this.data.tryToVerify) {
      this.#preselectNull();
      return;
    }

    // Try to preselect "something".
    if (this.assetDefects.length === 0) {
      this.#preselectNull();
      return; // No previous defect entries available.
    }

    const lastAssetDefect = this.assetDefects[0];
    if (lastAssetDefect.defectTypeId === DefectType.Verified) {
      this.#preselectNull();
      return; // Latest entry is already verified.
    }

    this.selectedKind = Array.from(this.kinds.values()).find((x) => x.id === DefectType.Verified);
    this.selectedDefectOption = Array.from(this.defectOptions.values()).find(
      (x) => x.id === lastAssetDefect.defectStateId
    );
    this.comment = lastAssetDefect.comment ?? undefined;
  }

  #preselectNull() {
    this.selectedKind = Array.from(this.kinds.values()).find((x) => x.id === DefectType.Verified);
  }

  async #verify() {
    if (!this.comment || !this.selectedDefectOption) {
      return;
    }

    this.activity = true;
    try {
      const variables: VerifyDefectMutatioNArgs = {
        data: {
          assetId: this.data.assetId,
          defectStateId: this.selectedDefectOption?.id,
          comment: this.comment,
        },
      };

      const result = await firstValueFrom(
        this._apollo.mutate<VerifyDefectMutationRoot>({
          mutation: gql`
            ${FULL_FRAGMENT_TENANT_DEFECT}
            mutation VerifyDefect($data: TenantDefectInputCreate!) {
              verifyDefect(data: $data) {
                ...FullFragmentTenantDefect
              }
            }
          `,
          variables,
          fetchPolicy: 'network-only',
          update: (cache, { data }) => {
            if (!data?.verifyDefect) {
              return;
            }
            this.#notifyLocally('verified', data.verifyDefect);
          },
        })
      );

      if (!result.data?.verifyDefect) {
        this._toastService.error('Could not create defect record.', 'ERROR');
      } else {
        this._dialogRef.close(result.data.verifyDefect);
      }
    } catch (error) {
      this._toastService.error(new CatchError(error).message, 'ERROR');
    } finally {
      this.activity = false;
    }
  }

  async #report() {
    if (!this.comment || !this.selectedDefectOption) {
      return;
    }

    this.activity = true;
    try {
      const variables: ReportDefectMutationArgs = {
        data: {
          assetId: this.data.assetId,
          defectStateId: this.selectedDefectOption?.id,
          comment: this.comment,
        },
      };

      const result = await firstValueFrom(
        this._apollo.mutate<reportDefectMutationRoot>({
          mutation: gql`
            ${FULL_FRAGMENT_TENANT_DEFECT}
            mutation ReportDefect($data: TenantDefectInputCreate!) {
              reportDefect(data: $data) {
                ...FullFragmentTenantDefect
              }
            }
          `,
          variables,
          fetchPolicy: 'network-only',
          update: (cache, { data }) => {
            if (!data?.reportDefect) {
              return;
            }
            this.#notifyLocally('reported', data.reportDefect);
          },
        })
      );

      if (!result.data?.reportDefect) {
        this._toastService.error('Could not create defect record.', 'ERROR');
      } else {
        this._dialogRef.close(result.data.reportDefect);
      }
    } catch (error) {
      this._toastService.error(new CatchError(error).message, 'ERROR');
    } finally {
      this.activity = false;
    }
  }

  #notifyLocally(action: 'reported' | 'verified', assetDefect: TenantDefectOutput) {
    const eventData: LocalEventData_AssetDefect = {
      filterSessionId: AppModule.sessionId,
      data: [
        {
          action,
          assetDefect,
        },
      ],
    };

    this._localEventService.emitNewEvent(LocalEventType.AssetDefect, eventData);
  }
}
